import { collection, documentId, getDocs, query, where } from "firebase/firestore";

/**
 * Fetch users by DOCUMENT ID, chunked to Firestore's 30-value `in` limit.
 *
 * Deliberately keyed on documentId() rather than the `userID` field, unlike
 * fetchUsersWithIDs below. Docs created by the become-a-host web funnel don't
 * always carry a `userID` field, so a field query silently omits exactly the
 * hosts an admin is most likely to be looking for. The doc id is always there.
 */
export const fetchUsersByDocIds = async (dbo, docIds) => {
  const ids = [...new Set((docIds || []).filter(Boolean))];
  if (ids.length === 0) return [];

  const chunks = [];
  for (let i = 0; i < ids.length; i += 30) chunks.push(ids.slice(i, i + 30));

  const snaps = await Promise.all(
    chunks.map((chunk) => getDocs(query(collection(dbo, "users"), where(documentId(), "in", chunk))))
  );
  return snaps.flatMap((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() })));
};

export const fetchUsersWithIDs = async (dbo, userIDs) => {
  if (userIDs == null || userIDs.length === 0) return [];

  const filteredUserIDs = userIDs.filter((uid) => uid != null);

  if (filteredUserIDs.length > 30) {
    const chunks = [];
    for (let i = 0; i < filteredUserIDs.length; i += 30) {
      chunks.push(filteredUserIDs.slice(i, i + 30));
    }

    const usersPromises = chunks.map((chunk) => fetchUserChunk(dbo, chunk));
    const usersArrays = await Promise.all(usersPromises);
    return usersArrays.flat();
  } else {
    return fetchUserChunk(dbo, filteredUserIDs);
  }
};

const fetchUserChunk = async (dbo, userIDs) => {
  const usersRef = collection(dbo, "users");
  const q = query(usersRef, where("userID", "in", userIDs));
  const querySnapshot = await getDocs(q);
  const users = [];
  querySnapshot.forEach((doc) => {
    users.push(doc.data());
  });
  return users;
};

// Build the router state used to pre-fill the "Create Event" form from an
// event suggestion. Shared by the Suggestions tab and the "Finish creating"
// section on the Upcoming tab so both surfaces hand the form identical input.
export const buildSuggestionConversionState = (suggestion) => {
  const parts = [];
  if (suggestion.eventName) parts.push(`Event: ${suggestion.eventName}`);
  if (suggestion.venue) parts.push(`Venue: ${suggestion.venue}`);
  if (suggestion.suggestedDate) parts.push(`Date: ${suggestion.suggestedDate.toDate().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}`);
  if (suggestion.suggestedTime) parts.push(`Time: ${suggestion.suggestedTime}`);
  if (suggestion.theme) parts.push(`Theme: ${suggestion.theme}`);
  if (suggestion.description) parts.push(`\n${suggestion.description}`);
  if (suggestion.reasoning) parts.push(`\nWhy: ${suggestion.reasoning}`);
  return {
    // Carried through the form so FormSection can flip this suggestion to
    // "converted" only once the event is actually created (not on handoff).
    suggestionId: suggestion.id,
    suggestionMessage: parts.join("\n"),
    suggestionImageUrl: suggestion.imageUrl || null,
  };
};
